import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { formatDateTime, prettify } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollText, RefreshCw, ShieldAlert, LogIn, LogOut, UserPlus, KeyRound, Download, Repeat, Eye } from "lucide-react";

const ACTION_OPTIONS = [
  { value: "all", label: "All activity" },
  { value: "login", label: "Sign in" },
  { value: "login_failed", label: "Failed sign in" },
  { value: "account_setup", label: "Account setup" },
  { value: "password_reset", label: "Password reset" },
  { value: "user_invited", label: "User invited" },
  { value: "user_created", label: "User created" },
  { value: "role_changed", label: "Role changed" },
  { value: "status_changed", label: "Status changed" },
  { value: "user_deactivated", label: "User deactivated" },
  { value: "portal_invite", label: "Portal invite" },
  { value: "document_download", label: "Document download" },
  { value: "portal_download", label: "Portal download" },
  { value: "lead_converted", label: "Lead converted" },
];

const ACTION_META = {
  login: { icon: LogIn, tone: "green" },
  logout: { icon: LogOut, tone: "gray" },
  login_failed: { icon: ShieldAlert, tone: "red" },
  account_setup: { icon: KeyRound, tone: "navy" },
  password_reset: { icon: KeyRound, tone: "amber" },
  user_invited: { icon: UserPlus, tone: "sky" },
  user_created: { icon: UserPlus, tone: "sky" },
  role_changed: { icon: Repeat, tone: "amber" },
  status_changed: { icon: Repeat, tone: "amber" },
  user_deactivated: { icon: ShieldAlert, tone: "red" },
  portal_invite: { icon: UserPlus, tone: "sky" },
  document_download: { icon: Download, tone: "navy" },
  portal_download: { icon: Download, tone: "navy" },
  lead_converted: { icon: Repeat, tone: "green" },
  view: { icon: Eye, tone: "gray" },
};

const TONES = {
  green: { bg: "rgba(47,125,90,0.12)", color: "#256B4B", border: "rgba(47,125,90,0.28)" },
  red: { bg: "rgba(156,30,34,0.10)", color: "#9C1E22", border: "rgba(156,30,34,0.24)" },
  amber: { bg: "rgba(183,121,31,0.14)", color: "#9A6516", border: "rgba(183,121,31,0.30)" },
  sky: { bg: "rgba(155,185,206,0.25)", color: "#1C4358", border: "rgba(155,185,206,0.45)" },
  navy: { bg: "rgba(28,67,88,0.10)", color: "#1C4358", border: "rgba(28,67,88,0.20)" },
  gray: { bg: "rgba(75,85,99,0.10)", color: "#4B5563", border: "rgba(75,85,99,0.22)" },
};

const ActionBadge = ({ action }) => {
  const meta = ACTION_META[action] || { icon: ScrollText, tone: "navy" };
  const tone = TONES[meta.tone] || TONES.navy;
  const Icon = meta.icon;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold border whitespace-nowrap"
      style={{ backgroundColor: tone.bg, color: tone.color, borderColor: tone.border }} data-testid="audit-action-badge">
      <Icon size={12} /> {prettify(action)}
    </span>
  );
};

const metaSummary = (m) => {
  if (!m || typeof m !== "object") return "";
  return Object.entries(m)
    .filter(([, v]) => v !== null && v !== undefined && v !== "")
    .map(([k, v]) => `${prettify(k)}: ${typeof v === "object" ? JSON.stringify(v) : v}`)
    .join(" · ");
};

export default function AuditLogs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (action && action !== "all") params.action = action;
      const { data } = await api.get("/audit-logs", { params });
      setItems(data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [action]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#252221] flex items-center gap-2">
            <ScrollText size={22} className="text-[#1C4358]" /> Audit Log
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">Security trail of sign-ins, account changes, and sensitive accesses.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="h-10 w-[190px]" data-testid="audit-action-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ACTION_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} data-testid="audit-refresh-button" className="h-10">
            <RefreshCw size={15} className="mr-1.5 text-[#1C4358]" /> Refresh
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-[#E6EDF2] bg-white overflow-hidden" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
        {loading ? (
          <div className="p-4 space-y-3">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center">
            <div className="mx-auto mb-3 h-14 w-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: "rgba(28,67,88,0.08)" }}>
              <ScrollText size={26} className="text-[#1C4358]" />
            </div>
            <p className="font-medium text-[#252221]">No audit events</p>
            <p className="text-sm text-muted-foreground">Activity will appear here as your team uses the Command Center.</p>
          </div>
        ) : (
          <div className="overflow-x-auto mmm-scroll">
            <Table data-testid="audit-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">When</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">User</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Action</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Resource</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Details</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">IP</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((a) => (
                  <TableRow key={a.id} className="hover:bg-[rgba(155,185,206,0.10)]" data-testid="audit-row">
                    <TableCell className="py-3 text-sm text-muted-foreground whitespace-nowrap">{formatDateTime(a.created_at)}</TableCell>
                    <TableCell className="text-sm">
                      <div className="font-medium text-[#252221] truncate max-w-[180px]">{a.user_email || "—"}</div>
                      <div className="text-[11px] text-muted-foreground">{prettify(a.user_role) || ""}</div>
                    </TableCell>
                    <TableCell><ActionBadge action={a.action} /></TableCell>
                    <TableCell className="text-sm text-[#252221]">
                      {a.resource_type ? (
                        <span>{prettify(a.resource_type)}{a.resource_id ? <span className="text-muted-foreground"> · {String(a.resource_id).slice(0, 8)}…</span> : null}</span>
                      ) : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[260px] truncate" title={metaSummary(a.meta)}>{metaSummary(a.meta) || "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{a.ip || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground mt-3">Showing the most recent {items.length} events{items.length >= 300 ? " (limit reached)" : ""}.</p>
    </div>
  );
}
